import React, { useCallback, useContext, useEffect, useState, FunctionComponent } from 'react';

import { CreateCustomFieldParams } from '@api/v4/assets/customFieldTypes';
import { getCustomFieldKeys } from '@api/v4/resources/custom_fields';
import { listCustomFieldValues } from '@api/v4/assets/custom_fields';

import { CustomFieldKeysResponseObject } from '@api/v4/resources/CustomFieldKeysTypes';

import assetModalContext from '@components/asset/modal/tabs/edit/asset_modal_context';
import {
  ActionTypes,
  CreateCustomFieldsDispatch,
  FlatCustomFieldMaps,
  FlattenedCustomFieldKeyValuesMap,
  InitialCustomFieldKeysDispatch,
  InitialCustomFieldMapsDataDispatch,
  InitialCustomFieldValuesDataDispatch,
  UpdateCustomFieldsDispatch,
} from '@components/asset/modal/tabs/edit/EditTabTypes';
import { BFLoader } from '@components/common/loader/main';
import { Locale } from '@components/common/language_menu/languagesMap';
import {
  AddUncontrolledCustomFieldButton
} from '@components/asset/modal/tabs/edit/main_pane/custom_fields/AddUncontrolledCustomField';
import { CustomFieldKeyValueRow } from './custom-field-row/CustomFieldKeyValueRow';

import './styles/_custom_fields.scss';

interface CustomFieldsSectionProps {
  initialData: {
    assetKey: string;
    customFieldKeys: CustomFieldKeysResponseObject[];
    flattenedCustomFieldKeyValuesMap: FlattenedCustomFieldKeyValuesMap;
  };
  editState: {
    customFieldMaps: FlatCustomFieldMaps[];
    createCustomFieldMaps: CreateCustomFieldParams[];
    flattenedCustomFieldKeyValuesMap: FlattenedCustomFieldKeyValuesMap;
  };
  ugtLocale: Locale | '';
}

interface CustomFieldsScopedContext {
  state: CustomFieldsSectionProps;
  dispatch: InitialCustomFieldKeysDispatch
    | CreateCustomFieldsDispatch
    | InitialCustomFieldMapsDataDispatch
    | InitialCustomFieldValuesDataDispatch
    | UpdateCustomFieldsDispatch;
}

export const CustomFieldsList: FunctionComponent = () => {
  const [newCustomFieldFocus, setNewCustomFieldFocus] = useState(false);
  const { state, dispatch }: CustomFieldsScopedContext = useContext(assetModalContext);
  const initialCustomFieldKeys = state?.initialData?.customFieldKeys;
  const editedCustomFieldMap = state?.editState?.flattenedCustomFieldKeyValuesMap;
  const controlledCustomFieldsEnabled = BFG.brandfolderSettings?.controlled_custom_fields_enabled;

  const getCustomFieldsAsync = useCallback(async (): Promise<void> => {
    const getCustomFieldKeysAsync = async (): Promise<void> => {
      const customFieldKeysParams = {
        fields: 'multi_value_enabled',
        order: 'asc',
        per: 3000,
        sort_by: 'name',
        ugt_locale: state.ugtLocale || undefined
      };
      const options = { params: customFieldKeysParams, resourceKey: BFG.resource.key, resourceType: BFG.resource.type };
      const response = await getCustomFieldKeys(options);
      const initialDispatch = dispatch as InitialCustomFieldKeysDispatch;
      initialDispatch({ type: ActionTypes.FetchCustomFieldKeys, payload: { response } });
    };

    const listCustomFieldValuesAsync = async (): Promise<void> => {
      const queryParams = {
        ugt_locale: state.ugtLocale || undefined,
        include: 'custom_field_key'
      };
      const response = await listCustomFieldValues(state.initialData.assetKey, queryParams);
      const initialDispatch = dispatch as InitialCustomFieldValuesDataDispatch;
      initialDispatch({ type: ActionTypes.FetchCustomFieldValues, payload: { response } });
    };

    if (!state.initialData.assetKey) {
      await getCustomFieldKeysAsync();
      const emptyCustomFieldValueList = { data: [] };
      const initialDispatch = dispatch as InitialCustomFieldValuesDataDispatch;
      initialDispatch({ type: ActionTypes.FetchCustomFieldValues, payload: { response: emptyCustomFieldValueList } });
    } else {
      await Promise.all([getCustomFieldKeysAsync(), listCustomFieldValuesAsync()]);
    }
  }, [state.initialData.assetKey, state.ugtLocale, dispatch]);

  useEffect(() => {
    if (state.initialData.customFieldKeys === null
      && state.initialData.flattenedCustomFieldKeyValuesMap === null
    ) {
      getCustomFieldsAsync();
    }
  }, [
    state.initialData.customFieldKeys,
    state.initialData.flattenedCustomFieldKeyValuesMap,
    getCustomFieldsAsync
  ]);

  if (!state.initialData.customFieldKeys || !state.initialData.flattenedCustomFieldKeyValuesMap) {
    return <BFLoader />;
  }

  // these are custom field keys that have been added through the UI
  // new custom field keys are only allowed to be added when custom fields
  // are not controlled
  const newUncontrolledCustomFieldKeys = editedCustomFieldMap
    ? Object.keys(editedCustomFieldMap).filter((cfkId) => (
      !initialCustomFieldKeys.some((initialKey) => initialKey.id === cfkId)
    )) : [];

  return (
    <div className="asset-modal-edit-custom-fields">
      <header className="custom-fields-list--heading">
        <div className="custom-fields-list--heading--key"><p>{bfTranslate('Key')}</p></div>
        <div className="custom-fields-list--heading--values"><p>{bfTranslate('Values')}</p></div>
      </header>
      <ul className={`custom-field-list bf-scroll-element ${!controlledCustomFieldsEnabled && 'max-height'}`}>
        {newUncontrolledCustomFieldKeys.map((keyId, index) => (
          <CustomFieldKeyValueRow
            key={keyId}
            controlledCustomFieldsEnabled={controlledCustomFieldsEnabled}
            customFieldKey={{
              attributes: {
                name: editedCustomFieldMap[keyId].customFieldKey.name,
                allowed_values: []
              },
              id: keyId,
              type: 'custom_field_keys'
            }}
            focused={index === newUncontrolledCustomFieldKeys.length - 1 && newCustomFieldFocus}
            setNewCustomFieldFocus={setNewCustomFieldFocus}
          />
        ))}
        {editedCustomFieldMap && initialCustomFieldKeys.map((key) => (
          <CustomFieldKeyValueRow
            key={key.id}
            controlledCustomFieldsEnabled={controlledCustomFieldsEnabled}
            customFieldKey={key}
          />
        ))}
      </ul>
      {!controlledCustomFieldsEnabled && (
        <AddUncontrolledCustomFieldButton setNewCustomFieldFocus={setNewCustomFieldFocus} />
      )}
    </div>
  );
};
